import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { categoryService } from '@/services/category.service';
import { CategoryWithSubCategories, CreateCategoryDto, CreateSubCategoryDto } from '@/types/category.types';
import { PlusIcon, PencilIcon, TrashIcon, FolderPlusIcon } from '@heroicons/react/24/outline';
import { Alert } from '@/components/common/Alert';
import { Badge } from '@/components/common/Badge';
import '@/styles/dashboard.css';

export const Categories = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<CategoryWithSubCategories[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // États pour les formulaires
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showSubCategoryForm, setShowSubCategoryForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<number | null>(null);
  const [editingSubCategory, setEditingSubCategory] = useState<number | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  
  // Formulaires
  const [categoryForm, setCategoryForm] = useState<CreateCategoryDto>({
    name: '',
    description: '',
    is_active: true,
  });
  
  const [subCategoryForm, setSubCategoryForm] = useState<CreateSubCategoryDto>({
    category_id: 0,
    name: '',
    description: '',
    is_active: true,
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await categoryService.getAll();
      setCategories(data);
      setError(null);
    } catch (err) {
      console.error('Erreur lors du chargement des catégories:', err);
      setError('Erreur lors du chargement des catégories');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async () => {
    if (!categoryForm.name.trim()) {
      alert(t('adminCategories.nameRequired'));
      return;
    }

    try {
      if (editingCategory) {
        await categoryService.update(editingCategory, categoryForm);
        alert(t('adminCategories.saveSuccess'));
      } else {
        await categoryService.create(categoryForm);
        alert(t('adminCategories.createSuccess'));
      }
      resetCategoryForm();
      await loadCategories();
    } catch (err: any) {
      console.error('Erreur:', err);
      alert(err?.response?.data?.error || 'Erreur lors de la sauvegarde');
    }
  };

  const handleCreateSubCategory = async () => {
    if (!subCategoryForm.name.trim()) {
      alert(t('adminCategories.subNameRequired'));
      return;
    }

    if (!subCategoryForm.category_id) {
      alert(t('adminCategories.selectParent'));
      return;
    }

    try {
      if (editingSubCategory) {
        await categoryService.updateSubCategory(editingSubCategory, subCategoryForm);
        alert(t('adminCategories.subSaveSuccess'));
      } else {
        await categoryService.createSubCategory(subCategoryForm);
        alert(t('adminCategories.subCreateSuccess'));
      }
      resetSubCategoryForm();
      await loadCategories();
    } catch (err: any) {
      console.error('Erreur:', err);
      alert(err?.response?.data?.error || 'Erreur lors de la sauvegarde');
    }
  };

  const handleEditCategory = (category: CategoryWithSubCategories) => {
    setCategoryForm({
      name: category.name,
      description: category.description || '',
      is_active: category.is_active,
    });
    setEditingCategory(category.id);
    setShowCategoryForm(true);
  };

  const handleEditSubCategory = (subCategory: any, categoryId: number) => {
    setSubCategoryForm({
      category_id: categoryId,
      name: subCategory.name,
      description: subCategory.description || '',
      is_active: subCategory.is_active,
    });
    setEditingSubCategory(subCategory.id);
    setSelectedCategoryId(categoryId);
    setShowSubCategoryForm(true);
  };

  const handleDeleteCategory = async (id: number, name: string) => {
    if (!window.confirm(t('adminCategories.deleteCategoryConfirm', { name }))) {
      return;
    }

    try {
      await categoryService.delete(id);
      alert(t('adminCategories.deleteSuccess'));
      await loadCategories();
    } catch (err: any) {
      console.error('Erreur:', err);
      alert(err?.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const handleDeleteSubCategory = async (id: number, name: string) => {
    if (!window.confirm(t('adminCategories.deleteSubConfirm', { name }))) {
      return;
    }

    try {
      await categoryService.deleteSubCategory(id);
      alert(t('adminCategories.subDeleteSuccess'));
      await loadCategories();
    } catch (err: any) {
      console.error('Erreur:', err);
      alert(err?.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const resetCategoryForm = () => {
    setCategoryForm({ name: '', description: '', is_active: true });
    setEditingCategory(null);
    setShowCategoryForm(false);
  };

  const resetSubCategoryForm = () => {
    setSubCategoryForm({ category_id: 0, name: '', description: '', is_active: true });
    setEditingSubCategory(null);
    setSelectedCategoryId(null);
    setShowSubCategoryForm(false);
  };

  const openSubCategoryForm = (categoryId: number) => {
    setSubCategoryForm(prev => ({ ...prev, category_id: categoryId }));
    setSelectedCategoryId(categoryId);
    setShowSubCategoryForm(true);
  };

  return (
    <>

    <div className="table-card">
              <div className="table-header">
                <h3 className="table-title">{t('adminCategories.title', { count: categories.length })}</h3>
                <div className="flex gap-2 items-center">
                  <button
                    onClick={() => {
                      resetCategoryForm();
                      setShowCategoryForm(true);
                    }}
                    className="btn btn-primary btn-sm"
                  >
                    <PlusIcon className="w-4 h-4" />
                    <span>{t('adminCategories.addCategory')}</span>
                  </button>
                </div>
              </div>

              {error && (
                <Alert type="error" message={error} />
              )}

              {loading ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  {t('adminCategories.loading')}
                </div>
              ) : categories.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  {t('adminCategories.empty')}
                </div>
              ) : (
                <div style={{ padding: '20px' }}>
                  {categories.map((category) => (
                    <div
                      key={category.id}
                      style={{
                        marginBottom: '24px',
                        padding: '20px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        background: 'white'
                      }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '16px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <h4 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#333', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <span style={{ wordBreak: 'break-word' }}>{category.name}</span>
                            {!category.is_active && (
                              <Badge variant="error">{t('adminCategories.inactive')}</Badge>
                            )}
                          </h4>
                          {category.description && (
                            <p style={{ margin: 0, color: '#666', fontSize: '14px', wordBreak: 'break-word' }}>
                              {category.description}
                            </p>
                          )}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          <button
                            onClick={() => openSubCategoryForm(category.id)}
                            className="btn btn-secondary btn-sm"
                            title={t('adminCategories.addSubcategoryTitle')}
                          >
                            <FolderPlusIcon className="w-4 h-4" />
                            <span>{t('adminCategories.addSubcategory')}</span>
                          </button>
                          <button
                            onClick={() => handleEditCategory(category)}
                            className="action-btn action-edit"
                            title={t('adminCategories.editCategoryAria')}
                            aria-label={t('adminCategories.editCategoryAria')}
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteCategory(category.id, category.name)}
                            className="action-btn action-delete"
                            title={t('adminCategories.deleteCategoryAria')}
                            aria-label={t('adminCategories.deleteCategoryAria')}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {category.sub_categories.length > 0 && (
                        <div style={{ marginTop: '16px', paddingLeft: '20px', borderLeft: '2px solid #e0e0e0' }}>
                          <h5 style={{ margin: 0, marginBottom: '12px', fontSize: '14px', fontWeight: 600, color: '#666' }}>
                            {t('adminCategories.subcategories', { count: category.sub_categories.length })}
                          </h5>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {category.sub_categories.map((subCategory) => (
                              <div
                                key={subCategory.id}
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '12px',
                                  padding: '12px',
                                  background: '#f9f9f9',
                                  borderRadius: '6px'
                                }}
                              >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <span style={{ fontSize: '14px', fontWeight: 500, color: '#333', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ wordBreak: 'break-word' }}>{subCategory.name}</span>
                                    {!subCategory.is_active && (
                                      <Badge variant="error">{t('adminCategories.inactive')}</Badge>
                                    )}
                                  </span>
                                  {subCategory.description && (
                                    <p style={{ marginTop: '4px', marginBottom: 0, color: '#666', fontSize: '12px', wordBreak: 'break-word' }}>
                                      {subCategory.description}
                                    </p>
                                  )}
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                  <button
                                    onClick={() => handleEditSubCategory(subCategory, category.id)}
                                    className="action-btn action-edit"
                                    title={t('adminCategories.editSubcategoryAria')}
                                    aria-label={t('adminCategories.editSubcategoryAria')}
                                  >
                                    <PencilIcon className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteSubCategory(subCategory.id, subCategory.name)}
                                    className="action-btn action-delete"
                                    title={t('adminCategories.deleteSubcategoryAria')}
                                    aria-label={t('adminCategories.deleteSubcategoryAria')}
                                  >
                                    <TrashIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Formulaire Catégorie */}
            {showCategoryForm && (
              <div className="modal-overlay">
                <div className="modal-content">
                  <div className="modal-header">
                    <h3>{editingCategory ? t('adminCategories.editModalTitle') : t('adminCategories.newModalTitle')}</h3>
                  </div>
                  <div style={{ padding: '24px' }}>
                    <div className="form-group">
                      <label className="form-label">
                        {t('adminCategories.categoryName')} <span style={{ color: '#f44336' }}>*</span>
                      </label>
                      <input
                        type="text"
                        value={categoryForm.name}
                        onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder={t('adminCategories.namePlaceholder')}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        {t('adminCategories.description')}
                      </label>
                      <textarea
                        value={categoryForm.description}
                        onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                        placeholder={t('adminCategories.descriptionPlaceholder')}
                        rows={3}
                        className="form-input"
                        style={{ resize: 'vertical' }}
                      />
                    </div>
                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={categoryForm.is_active}
                          onChange={(e) => setCategoryForm(prev => ({ ...prev, is_active: e.target.checked }))}
                          style={{ width: '16px', height: '16px' }}
                        />
                        <span>{t('adminCategories.active')}</span>
                      </label>
                    </div>
                    <div className="form-actions">
                      <button
                        onClick={resetCategoryForm}
                        className="btn btn-secondary"
                      >
                        {t('adminCategories.cancel')}
                      </button>
                      <button
                        onClick={handleCreateCategory}
                        className="btn btn-primary"
                      >
                        {editingCategory ? t('adminCategories.update') : t('adminCategories.create')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Modal Formulaire Sous-catégorie */}
            {showSubCategoryForm && (
              <div className="modal-overlay">
                <div className="modal-content">
                  <div className="modal-header">
                    <h3>{editingSubCategory ? t('adminCategories.editSubModalTitle') : t('adminCategories.newSubModalTitle')}</h3>
                  </div>
                  <div style={{ padding: '24px' }}>
                    <div className="form-group">
                      <label className="form-label">
                        {t('adminCategories.parentCategory')} <span style={{ color: '#f44336' }}>*</span>
                      </label>
                      <select
                        value={subCategoryForm.category_id}
                        onChange={(e) => setSubCategoryForm(prev => ({ ...prev, category_id: parseInt(e.target.value) }))}
                        disabled={!!selectedCategoryId}
                        className="form-select"
                        style={selectedCategoryId ? { backgroundColor: '#f5f5f5' } : {}}
                      >
                        <option value="0">{t('adminCategories.selectCategory')}</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        {t('adminCategories.subCategoryName')} <span style={{ color: '#f44336' }}>*</span>
                      </label>
                      <input
                        type="text"
                        value={subCategoryForm.name}
                        onChange={(e) => setSubCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder={t('adminCategories.subNamePlaceholder')}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        {t('adminCategories.description')}
                      </label>
                      <textarea
                        value={subCategoryForm.description}
                        onChange={(e) => setSubCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                        placeholder={t('adminCategories.subDescriptionPlaceholder')}
                        rows={3}
                        className="form-input"
                        style={{ resize: 'vertical' }}
                      />
                    </div>
                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={subCategoryForm.is_active}
                          onChange={(e) => setSubCategoryForm(prev => ({ ...prev, is_active: e.target.checked }))}
                          style={{ width: '16px', height: '16px' }}
                        />
                        <span>{t('adminCategories.subActive')}</span>
                      </label>
                    </div>
                    <div className="form-actions">
                      <button
                        onClick={resetSubCategoryForm}
                        className="btn btn-secondary"
                      >
                        {t('adminCategories.cancel')}
                      </button>
                      <button
                        onClick={handleCreateSubCategory}
                        className="btn btn-primary"
                      >
                        {editingSubCategory ? t('adminCategories.update') : t('adminCategories.create')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
  
    </>
  );
};
